// Central transfer-limits configuration. Single source of truth for the
// numbers documented in docs/features/beam/F14.3-transfer-limits.md.
//
// Why a central module: F11.1 (chunked send), F14.2 (receiver consent), and
// signaling.js all gate behavior on these numbers. A magic-number sprawl
// across modules would mean the next bump would require code archaeology to
// find every call site.
//
// F11.4 (cloud fallback via Firebase Storage) was cancelled 2026-05-29 —
// files transfer over LAN only. There is no second `CLOUD_MAX_BYTES` cap.

export const LIMITS = Object.freeze({
  // Max in-flight transfers per (fromId, toId) pair. Sender's 6th outgoing
  // offer is rejected client-side with a clear toast.
  CONCURRENT_PER_PAIR: 5,

  // LAN (WebRTC) — refuse to send files larger than 2 GB. Browsers will
  // happily try and OOM the tab; we'd rather fail fast.
  LAN_MAX_BYTES: 2 * 1024 * 1024 * 1024, // 2 GB

  // Signaling rate limit: token-bucket sized at 20 writes / 10 s per room.
  // Above this, writes queue locally; above SIGNAL_QUEUE_OVERFLOW the queue
  // drops with a toast.
  SIGNAL_TOKENS_PER_INTERVAL: 20,
  SIGNAL_INTERVAL_MS: 10_000,
  SIGNAL_QUEUE_OVERFLOW: 200,

  // Receiver-side dedupe — duplicate offers with the same id from the same
  // sender within this window are silently ignored (replay defense).
  OFFER_DEDUPE_WINDOW_MS: 5_000,

  // Mesh size cap — inherited from the legacy LAN doc, not the new design.
  // Beyond this, new peer connections are refused; the caller surfaces
  // "Mesh full" UI.
  PEER_MESH_CAP: 6,
});

/**
 * Resolve the size cap for a transfer. Files are LAN-only post-F11.4-cancel,
 * so this is effectively a constant — kept as a function so future transports
 * (if any) can re-introduce a switch without churning every call site.
 *
 * @returns {number} max bytes
 */
export function maxBytesFor() {
  return LIMITS.LAN_MAX_BYTES;
}

/**
 * Single-shot pre-send gate. Returns `{ allowed, reason? }` so callers can
 * surface a specific toast.
 *
 * @param {{ size: number, pairInFlight: number }} args
 * @returns {{ allowed: boolean, reason?: string }}
 */
export function checkLimits({ size, pairInFlight }) {
  if (pairInFlight >= LIMITS.CONCURRENT_PER_PAIR) {
    return { allowed: false, reason: 'Too many transfers in progress with this peer' };
  }
  const cap = maxBytesFor();
  if (size > cap) {
    const gb = (cap / (1024 * 1024 * 1024)).toFixed(0);
    return { allowed: false, reason: `File exceeds ${gb} GB LAN limit` };
  }
  return { allowed: true };
}
